/**
 * Promise-based confirmation dialogs (rendered once by <ConfirmHost/>):
 *
 *   const answer = await confirm({ title: "Delete X?", reason: true, destructive: true });
 *   if (!answer) return;           // cancelled
 *   await api(answer.reason);
 *
 * `reason: true` asks for an audit reason (min 3 characters); `typeToConfirm`
 * requires typing a value exactly (high-risk actions).
 */

import { reactive } from "vue";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  destructive?: boolean;
  reason?: boolean | { label?: string; placeholder?: string; minLength?: number };
  typeToConfirm?: string;
}

export interface ConfirmAnswer {
  reason: string;
}

interface State {
  open: boolean;
  options: ConfirmOptions | null;
  resolve: ((answer: ConfirmAnswer | null) => void) | null;
}

export const confirmState = reactive<State>({ open: false, options: null, resolve: null });

export function confirm(options: ConfirmOptions): Promise<ConfirmAnswer | null> {
  confirmState.resolve?.(null);
  return new Promise((resolve) => {
    confirmState.options = options;
    confirmState.resolve = resolve;
    confirmState.open = true;
  });
}

export function settleConfirm(answer: ConfirmAnswer | null): void {
  const resolve = confirmState.resolve;
  confirmState.open = false;
  confirmState.resolve = null;
  resolve?.(answer);
}
